//  src/components/first_screen/TimePanel.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { fetchTimeFromCoords } from "../../services/timeService";
import { getHijriDateCached, peekHijriDate } from "../../services/hijriService";
import { getNextPrayerOrJummah } from "../../utils/prayerUtils";
import { splitMeridiem } from "../../utils/time";
import { useClock } from "../../shared/clock/useClock";

// ======= QUICK TEST MODES =======================================
// MODE: "off"  -> live time
//       "fixed"-> frozen time (never moves)
//       "sim"  -> start at a hard-coded time, then tick forward like real time
const TEST_MODE = "off"; // "off" | "fixed" | "sim"
const TEST_FIXED_ISO = "2025-11-05T13:35:00+05:00";
const TEST_SIM_START_ISO = "2025-11-05T16:43:00+05:00";
// ===============================================================

const getOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const TimePanel = ({
  colorSettings = {},
  prayerData,
  onNextEventChange,
  onTimezoneChange,
}) => {
  const {
    headerBackgroundColor = "#ffffff",
    headerTextColor = "#000000",
    headerTimingColor = "#000000",
    highlightColor = "#D39B51",
  } = colorSettings;

  const [currentTime, setCurrentTime] = useState(moment());
  const [iqamahTimes, setIqamahTimes] = useState({});
  const [jummahTimes, setJummahTimes] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [hijriDate, setHijriDate] = useState("");
  const [masjidTimeZone, setMasjidTimeZone] = useState(moment.tz.guess());

  const lastDateIsoRef = useRef(null);

  // Initialize with provided prayerData
  useEffect(() => {
    if (!prayerData) return;

    if (prayerData.iqamatimes) {
      setIqamahTimes(prayerData.iqamatimes);
    }

    if (Array.isArray(prayerData.jummahTimes) && prayerData.jummahTimes.length > 0) {
      const sorted = [...prayerData.jummahTimes].sort((a, b) => a.sequence - b.sequence);
      setJummahTimes(sorted);
    } else {
      setJummahTimes([]); // ✅ clears stale previous jummah times
    }

    (async () => {
      let now = moment();
      let tz = masjidTimeZone;

      if (prayerData.startTimes?.latitude && prayerData.startTimes?.longitude) {
        try {
          const timeData = await fetchTimeFromCoords(
            prayerData.startTimes.latitude,
            prayerData.startTimes.longitude
          );
          tz = timeData.zoneName || tz;
          setMasjidTimeZone(tz);
          onTimezoneChange?.(tz);

          // Respect testing modes
          if (TEST_MODE === "fixed") {
            now = moment.tz(TEST_FIXED_ISO, tz);
          } else if (TEST_MODE === "sim") {
            now = moment.tz(TEST_SIM_START_ISO, tz);
          } else {
            now = moment.tz(timeData.dateTime || timeData.formatted, tz);
          }
        } catch (err) {
          console.warn("Fallback to local time (timezone fetch failed)", err);
          if (TEST_MODE === "fixed") now = moment(TEST_FIXED_ISO);
          else if (TEST_MODE === "sim") now = moment(TEST_SIM_START_ISO);
        }
      } else {
        // No coords, but still respect testing modes
        if (TEST_MODE === "fixed") now = moment(TEST_FIXED_ISO);
        else if (TEST_MODE === "sim") now = moment(TEST_SIM_START_ISO);
      }

      setCurrentTime(now);

      // Hijri (instant from cache, then background refresh)
      const dateIso = moment.tz(now, tz).format("YYYY-MM-DD");
      lastDateIsoRef.current = dateIso;

      const peek = peekHijriDate(dateIso, tz);
      setHijriDate(peek.hijri);

      getHijriDateCached(dateIso, tz, {
        background: true,
        lat: prayerData.startTimes?.latitude ?? null,
        long: prayerData.startTimes?.longitude ?? null,
      })
        .then(({ data }) => {
          if (lastDateIsoRef.current === dateIso) setHijriDate(data.hijri);
        })
        .catch(() => {});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prayerData]);

  // Global clock + simple test overrides
  const clockNow = useClock();

  // For "sim" mode, anchor real start and sim start (once)
  const simAnchorsRef = useRef({ realStartMs: null, simStartMs: null });
  if (TEST_MODE === "sim" && simAnchorsRef.current.realStartMs == null) {
    simAnchorsRef.current.realStartMs = clockNow;
    const parsed = Date.parse(TEST_SIM_START_ISO);
    simAnchorsRef.current.simStartMs = Number.isNaN(parsed) ? clockNow : parsed;
  }

  // Effective "now" (ms) used by the panel
  const effectiveNowMs = useMemo(() => {
    if (TEST_MODE === "fixed") {
      const t = Date.parse(TEST_FIXED_ISO);
      return Number.isNaN(t) ? clockNow : t;
    }
    if (TEST_MODE === "sim") {
      const { realStartMs, simStartMs } = simAnchorsRef.current;
      const elapsed = clockNow - (realStartMs ?? clockNow);
      return (simStartMs ?? clockNow) + elapsed;
    }
    // live
    return clockNow;
  }, [clockNow]);

  // Recompute derived state on each tick (or when deps change)
  useEffect(() => {
    if (!masjidTimeZone) return;

    const now = moment.tz(effectiveNowMs, masjidTimeZone);
    setCurrentTime(now);

    const next = getNextPrayerOrJummah(now, iqamahTimes, jummahTimes);
    setNextEvent(next);
    onNextEventChange?.(next);

    const dateIso = now.format("YYYY-MM-DD");
    if (dateIso !== lastDateIsoRef.current) {
      lastDateIsoRef.current = dateIso;
      const peek = peekHijriDate(dateIso, masjidTimeZone);
      setHijriDate(peek.hijri);
      getHijriDateCached(dateIso, masjidTimeZone, { background: true })
        .then(({ data }) => {
          if (lastDateIsoRef.current === dateIso) setHijriDate(data.hijri);
        })
        .catch(() => {});
    }
  }, [effectiveNowMs, masjidTimeZone, iqamahTimes, jummahTimes, onNextEventChange]);

  // Prevent the initial "0:00:00" flash
  const countdown = useMemo(() => {
    if (!nextEvent) return null;
    const ms = nextEvent.time.diff(currentTime);
    if (ms <= 0) return "0:00:00";
    const dur = moment.utc(ms);
    return dur.format("H:mm:ss");
  }, [nextEvent, currentTime]);

  const renderJummahBlock = (entry) => {
    const isNext = nextEvent?.type === "jummah" && entry.sequence === nextEvent.sequence;

    return (
      <div key={entry.sequence} className="w-1/3 px-[0.8svh]">
        

        <div
          className="border-[0.3svh] border-black py-0 font-sans"
          style={{ backgroundColor: isNext ? highlightColor : headerBackgroundColor }}
        >

          <div
            className="font-semibold text-[3.3svh] mb-[0.8svh]"
            style={{
              backgroundColor: isNext ? highlightColor : headerBackgroundColor,
              color: isNext ? "#fff" : headerTextColor,
            }}
          >
            {getOrdinal(entry.sequence)}
          </div>

          <hr className="border-[0.1svh] border-black mx-[5svh] mb-[0.8svh]" />

          <p
            className="font-medium text-[3.3svh]"
            style={{ color: isNext ? "#fff" : headerTimingColor }}
          >
            {(() => {
              const r = splitMeridiem(entry.khutba_Time);
              if (!r) return entry.khutba_Time;
              return (
                <>
                  <span>{r.main}</span>
                  <span className="text-[2svh] ml-[0.2svh]">{r.mer}</span>
                </>
              );
            })()}
          </p>

          <p
            className="font-medium text-[2svh]"
            style={{ color: isNext ? "#fff" : headerTextColor }}
          >
            KHUTBA
          </p>

          <hr className="border-[0.1svh] border-black mx-[5svh] mt-[1svh] mb-[0.5svh]" />

          <p
            className="font-medium text-[3.3svh]"
            style={{ color: isNext ? "#fff" : headerTimingColor }}
          >
            {(() => {
              const r = splitMeridiem(entry.iqama_Time);
              if (!r) return entry.iqama_Time;
              return (
                <>
                  <span>{r.main}</span>
                  <span className="text-[2svh] ml-[0.2svh]">{r.mer}</span>
                </>
              );
            })()}
          </p>
          <p
            className="font-medium text-[2svh] mb-[0.5svh]"
            style={{ color: isNext ? "#fff" : headerTextColor }}
          >
            IQAMAH
          </p>
        </div>
      </div>
    );
  };

  return (
    <div
      className="px-[0.8svh]"
      style={{ backgroundColor: headerBackgroundColor, color: headerTextColor }}
    >
      {/* Date */}
      <div className="text-center leading-[0.7]">
        <p className="font-sans uppercase font-semibold text-[3svh] mb-[2svh]">
          {hijriDate}
        </p>
        <p className="uppercase font-medium text-[3svh]">
          {currentTime.format("dddd, MMM D")}
        </p>
      </div>

      <hr className="w-full border-t-[0.47svh] border-black mt-[2.5svh]" />

      {/* Time */}
      <div className="flex justify-center items-end gap-1">
        <p
          className="text-[6.8svh] font-medium font-sans m-0"
          style={{ color: headerTextColor }}
        >
          {currentTime.format("hh:mm").replace(/^0(\d):/, "$1:")}
          <span
            className="text-[3.5svh] font-mono m-0 leading-6 ml-[0.5svh]"
            style={{ color: headerTimingColor }}
          >
            {currentTime.format("A")}
          </span>
        </p>
      </div>

      <hr className="w-full border-t-[0.47svh] border-black" />

      {/* Countdown */}
      <div className="text-center">
        <p className="text-[3svh] font-semibold mt-[2svh]">NEXT IQAMAH IN</p>
        <p
          className="text-[6svh] font-mono font-semibold my-0"
          style={{ color: headerTimingColor }}
        >
          {countdown !== null && <span>{countdown}</span>}
        </p>
      </div>

      <hr className="w-full border-t-[0.47svh] border-black mb-[1.5svh]" />

      {/* Jumu'ah */}
      {Array.isArray(jummahTimes) && jummahTimes.length > 0 && (
        <div className="text-center">
          <h3 className="font-semibold text-[3svh] mb-[0.5svh] mt-0">JUMU'AH</h3>
          <div className="flex flex-wrap justify-center">
            {jummahTimes.map(renderJummahBlock)}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePanel;
